import { useEffect, useRef, useState } from 'react';
import logo from './assets/images/gln-mark.svg';
import ContactForms from './components/ContactForms';
import './App.css';

const STEPS = [
  {
    num: '01',
    title: 'מצטרפים לרשת',
    text: 'מנכ״לים ומנכ״ליות נכנסים למרחב סגור, איכותי וממוקד — בלי רעש ובלי ג׳ארגון מיותר.',
  },
  {
    num: '02',
    title: 'נפתחים חיבורים',
    text: 'המערכת מחברת בין אנשים עם הקשרים הנכונים: תעשייה, אתגר, הזדמנות או שאיפה משותפת.',
  },
  {
    num: '03',
    title: 'נולדים שיתופי פעולה',
    text: 'משיחה קצרה צומחים שותפויות, המלצות, פתיחות דלת — והזדמנויות שלא קורות במקרה.',
  },
];

const VALUES = [
  {
    title: 'חיבורים מדויקים',
    text: 'לא עוד רשת חברתית כללית. כאן נפגשים מנהיגים שמדברים אותה שפה ומבינים את הלחץ של התפקיד.',
  },
  {
    title: 'שיתופי פעולה אמיתיים',
    text: 'מטרת הרשת היא תוצאה: שותפות עסקית, ייעוץ הדדי, פתיחת דלתות והזדמנויות שצומחות מאמון.',
  },
  {
    title: 'קהילה של מנהיגות',
    text: 'מרחב שבו מנכ״לים לא עומדים לבד — אלא בונים יחד רשת השפעה חכמה, אנושית וחזקה יותר.',
  },
];

function NetworkCanvas() {
  // Keep decorative nodes outside the center content zone (~x 520–920, y 120–520)
  // so they don't collide with the logo and headline.
  const nodes = [
    [110, 180, 1],
    [280, 90, 1.15],
    [460, 48, 1],
    [720, 36, 0.95],
    [980, 55, 1.1],
    [1180, 130, 1.2],
    [1340, 260, 1.05],
    [1280, 460, 1.15],
    [1380, 620, 1],
    [1120, 740, 1.1],
    [720, 820, 1],
    [380, 760, 1.05],
    [160, 620, 1.15],
    [70, 400, 1],
    [230, 300, 1.1],
  ];

  return (
    <svg
      className="network-canvas"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffc915" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#ffc915" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ffc915" stopOpacity="0" />
          <stop offset="50%" stopColor="#ffc915" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#94b0dd" stopOpacity="0" />
        </linearGradient>
      </defs>

      <g className="network-lines" stroke="url(#lineGrad)" strokeWidth="1.2" fill="none">
        <line className="pulse-line" x1="110" y1="180" x2="280" y2="90" />
        <line className="pulse-line d1" x1="280" y1="90" x2="460" y2="48" />
        <line className="pulse-line d2" x1="460" y1="48" x2="720" y2="36" />
        <line className="pulse-line d3" x1="720" y1="36" x2="980" y2="55" />
        <line className="pulse-line" x1="980" y1="55" x2="1180" y2="130" />
        <line className="pulse-line d1" x1="1180" y1="130" x2="1340" y2="260" />
        <line className="pulse-line d2" x1="1340" y1="260" x2="1280" y2="460" />
        <line className="pulse-line d3" x1="1280" y1="460" x2="1380" y2="620" />
        <line className="pulse-line" x1="1380" y1="620" x2="1120" y2="740" />
        <line className="pulse-line d1" x1="1120" y1="740" x2="720" y2="820" />
        <line className="pulse-line d2" x1="720" y1="820" x2="380" y2="760" />
        <line className="pulse-line d3" x1="380" y1="760" x2="160" y2="620" />
        <line className="pulse-line" x1="160" y1="620" x2="70" y2="400" />
        <line className="pulse-line d1" x1="70" y1="400" x2="110" y2="180" />
        <line className="pulse-line d2" x1="110" y1="180" x2="230" y2="300" />
        <line className="pulse-line d3" x1="230" y1="300" x2="70" y2="400" />
        <line className="pulse-line" x1="1180" y1="130" x2="1280" y2="460" />
        <line className="pulse-line d1" x1="160" y1="620" x2="380" y2="760" />
      </g>

      {nodes.map(([x, y, s], i) => (
        <g key={i} className={`network-node n${i % 4}`} transform={`translate(${x} ${y}) scale(${s})`}>
          <circle r="18" fill="url(#nodeGlow)" opacity="0.35" />
          <circle r="6" fill="#ffc915" />
          <circle r="3" fill="#fffef5" />
        </g>
      ))}
    </svg>
  );
}

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.18 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

function Reveal({ children, className = '' }) {
  const { ref, visible } = useReveal();
  return (
    <div ref={ref} className={`reveal ${visible ? 'is-visible' : ''} ${className}`.trim()}>
      {children}
    </div>
  );
}

function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="App">
      <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
        <a className="brand-lockup" href="#top" aria-label="Great Leaders Network">
          <img src={logo} alt="" className="brand-mark" />
          <span className="brand-word">Great Leaders Network</span>
        </a>
        <nav className="site-nav" aria-label="ניווט ראשי">
          <a href="#about">על הרשת</a>
          <a href="#how">איך זה עובד</a>
          <a href="#value">הערך</a>
          <a className="nav-cta" href="#join">
            הצטרפות
          </a>
        </nav>
      </header>

      <main id="top">
        <section className="hero" aria-label="פתיחה">
          <div className="hero-visual" aria-hidden="true">
            <div className="hero-wash" />
            <NetworkCanvas />
          </div>

          <div className="hero-content">
            <img src={logo} alt="לוגו Great Leaders Network" className="hero-logo" />
            <h1 className="hero-brand">
              Great Leaders
              <span>Network</span>
            </h1>
            <p className="hero-headline">חיבורים ושיתופי פעולה בין מנכ״לים</p>
            <p className="hero-support">
              הפלטפורמה שמחברת מנהיגים עסקיים לשיחות מדויקות, הזדמנויות אמיתיות ורשת שמשנה את המשחק.
            </p>
            <div className="hero-actions">
              <a className="btn btn-primary" href="#join">
                הצטרפו לרשת
              </a>
              <a className="btn btn-ghost" href="#about">
                גלו איך זה עובד
              </a>
            </div>
          </div>

          <a className="scroll-cue" href="#about" aria-label="גללו למטה">
            <span />
          </a>
        </section>

        <section className="section about" id="about">
          <Reveal>
            <p className="section-kicker">למה אנחנו כאן</p>
            <h2 className="section-title">כי מנכ״לים לא צריכים עוד קשרים — הם צריכים את הקשר הנכון</h2>
            <p className="section-lead">
              Great Leaders Network נולדה מתוך ההבנה שמנהיגות בצמרת היא תפקיד בודד מדי.
              אנחנו בונים מרחב שמחבר בין מנכ״לים ומנכ״ליות ליצירת שיתופי פעולה, פתיחת דלתות וצמיחה משותפת.
            </p>
          </Reveal>
        </section>

        <section className="section how" id="how">
          <Reveal>
            <p className="section-kicker">איך זה עובד</p>
            <h2 className="section-title">שלושה צעדים. חיבור אחד מדויק.</h2>
          </Reveal>
          <ol className="steps">
            {STEPS.map((step, i) => (
              <li key={step.num} className={`step step-${i + 1}`}>
                <Reveal>
                  <span className="step-num">{step.num}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ol>
        </section>

        <section className="section value" id="value">
          <Reveal>
            <p className="section-kicker">מה מקבלים</p>
            <h2 className="section-title">רשת שעובדת בשביל מי שמוביל</h2>
          </Reveal>
          <div className="value-list">
            {VALUES.map((item) => (
              <Reveal key={item.title} className="value-item">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="join" id="cta">
          <Reveal>
            <img src={logo} alt="" className="join-logo" />
            <h2>מוכנים להיכנס לרשת?</h2>
            <p>
              הצטרפו ל־Great Leaders Network והתחילו ליצור חיבורים שפותחים הזדמנויות —
              לא עוד נטוורקינג שטחי, אלא שיתופי פעולה עם משמעות.
            </p>
            <a className="btn btn-primary" href="#join">
              הורדה או יצירת קשר
            </a>
          </Reveal>
        </section>

        <ContactForms />
      </main>

      <footer className="site-footer">
        <div className="footer-brand">
          <img src={logo} alt="" />
          <div>
            <strong>Great Leaders Network</strong>
            <span>חיבורים ושיתופי פעולה בין מנכ״לים</span>
          </div>
        </div>
        <p className="footer-copy">© {new Date().getFullYear()} Great Leaders Network Ltd.</p>
      </footer>
    </div>
  );
}

export default App;
